/**
 * FD (fijo discontinuo) contract statuses that are exclusive to discontinuous contracts.
 *
 * During an inactivity period, the Pause interactor sets `terminated_on` on the employee
 * to revoke app access, making these employees appear as 'terminated' in the accessStatus
 * dimension. When filtering exclusively by these statuses the contractStatus filter is the
 * sole authority, so the default accessStatus guard must be bypassed.
 */
export const FD_EXCLUSIVE_CONTRACT_STATUSES = ['in_activity_period', 'in_inactivity_period']

/**
 * Returns true only when *every* selected contractStatus value is an FD-exclusive status.
 *
 * We intentionally use `every` (not `some`) so that a mixed selection such as
 * `['ongoing', 'in_inactivity_period']` still applies the default accessStatus guard –
 * 'ongoing' can match non-FD employees, and we must not unintentionally include terminated
 * employees that match the non-FD portion of the filter.
 */
export const isFdOnlyContractFilter = (contractStatus: string[] | undefined): boolean =>
  !!contractStatus?.length &&
  contractStatus.every((s) => FD_EXCLUSIVE_CONTRACT_STATUSES.includes(s))

/**
 * Resolves the effective `accessStatus` variable to send to the employees query.
 *
 * Rules (in priority order):
 * 1. If the caller explicitly selected access statuses, use them as-is.
 * 2. If the selected contract statuses are exclusively FD-only values, omit accessStatus
 *    entirely (`undefined`) so the contractStatus filter is the sole authority.
 * 3. If the current user can see former employees and no explicit filter is set, restrict to
 *    non-terminated statuses.
 * 4. Otherwise return `undefined` (no restriction).
 */
export const resolveAccessStatusWithDefault = ({
  accessStatus,
  contractStatus,
  canSeeFormerEmployees,
}: {
  accessStatus: string[] | undefined
  contractStatus: string[] | undefined
  canSeeFormerEmployees: boolean
}): string[] | undefined => {
  if (accessStatus?.length) return accessStatus
  if (isFdOnlyContractFilter(contractStatus)) return undefined
  if (canSeeFormerEmployees) return ['not_invited', 'accepted', 'active', 'invited']
  return undefined
}
